// src/components/reader/SectionsSheet.jsx
//
// Mobile bottom sheet holding the reader's FULL nav (lesson-3a-layout). It takes the
// nav as `children`, so both readers hand it the very same JSX their desktop sidebar
// renders -- groups, ticks, progress bar, practice entries, the Start-here chip. One
// nav, two presentations; nothing is duplicated and nothing can drift.
//
// Portalled to document.body so the fixed overlay is positioned against the VIEWPORT
// rather than the reader shell's transformed .page-fade-in ancestor -- the same
// reason EssayModal and ReaderAiDrawer portal.
import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ReadingPrefsContext } from '../../pages/useReadingPrefs.js';
import { portalSkinClass } from '../../pages/readingPrefs.js';
import './lesson.css';

export default function SectionsSheet({ open, onClose, title = 'Sections', children }) {
    const { colorMode } = React.useContext(ReadingPrefsContext);
    const skin = portalSkinClass(colorMode);

    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    if (!open) return null;

    return createPortal(
        <div className={skin}>
            <div className="btl-sheet-backdrop lg:hidden" onClick={onClose} aria-hidden="true" />
            <div className="btl-sheet lg:hidden" role="dialog" aria-modal="true" aria-label={title}>
                <div className="btl-sheet-grip" aria-hidden="true" />
                {/* No visible title: the nav inside already labels itself ("Sections" /
                    "Free preview"), and repeating it wasted a line on a phone. The sheet
                    is still named for screen readers via aria-label above. */}
                <div className="flex items-center justify-end px-4 pb-1 flex-shrink-0">
                    <button type="button" onClick={onClose}
                        className="font-display text-book-muted text-xs font-semibold hover:text-book-accent px-2 py-1">
                        Close
                    </button>
                </div>
                {/* The nav itself, exactly as the sidebar renders it. Taps inside close
                    the sheet via the readers' existing onSelect handlers. */}
                <div className="btl-sheet-body px-2 pb-4">{children}</div>
            </div>
        </div>,
        document.body,
    );
}
